// @flow

import * as React from 'react';
import * as Sentry from '@sentry/react';
import {isEqual} from 'lodash';

import type {Navigation} from 'docs/data/navigation/Navigation';
import {tree} from '../../generated_tree';
import {normalizeCommand} from '../cli/general';
import {giveSuggestion} from '../cli/suggestions';
import {MAX_PANE_COUNT} from '../constants';
import type {Param, Command} from '../commands/resource';
import {InputCommand} from '../../terminal/command';

type CluiOpenState = 'closed' | 'open';

export type SuggestionsType =
  | Array<[string, Command]>
  | Array<[string, Param]>
  | Array<[string, {}]>;

export type PaneState = {
  paneID: number,
  command: string,
  inputCommand: InputCommand,
  cursorIndex: number,
  history: Array<{
    command: string,
    output: React.Node,
    isPrintOutput?: boolean,
  }>,
  arrowHistory: Array<{command: string, output: React.Node}>,
  alteredHistory: Array<{command: string, output: React.Node}>,
  alteredIndex: number,
  runCommand: boolean,
  suggestions: SuggestionsType,
  suggestionIndex: number,
  allowInput: boolean,
  displayOn: 'input' | 'suggestion',
  showWelcomePrompt: boolean,
};

export type State = {
  openState: CluiOpenState,
  panes: Array<PaneState>,
  focusedPane: number,
  isFocused: boolean,
  locked: boolean,
  nextPaneID: number,
  showApiExplorer: boolean,
};

export const createInitialPaneState = ({
  paneID,
}: {
  paneID: number,
}): PaneState => {
  const emptySuggestions: Array<[string, {}]> = [];

  return {
    paneID,
    command: '',
    inputCommand: new InputCommand(''),
    cursorIndex: 0,
    history: [],
    arrowHistory: [],
    alteredHistory: [{command: '', output: ''}],
    alteredIndex: 0,
    runCommand: false,
    suggestions: emptySuggestions,
    suggestionIndex: -1,
    allowInput: true,
    displayOn: 'input',
    showWelcomePrompt: false,
  };
};

export const createInitialState = (): State => ({
  openState: 'closed',
  panes: [{...createInitialPaneState({paneID: 0}), showWelcomePrompt: true}],
  focusedPane: 0,
  isFocused: false,
  locked: false,
  nextPaneID: 1,
  showApiExplorer: false,
});

export const initialState = createInitialState();

export type FocusAction = {|type: 'SET_FOCUS', payload: boolean|};
export type AddPaneAction = {|type: 'ADD_PANE'|};
export type RemovePaneAction = {|type: 'REMOVE_PANE', payload: number|};
export type FocusPaneAction = {|type: 'FOCUS_PANE', payload: number|};
export type OpenAction = {|type: 'OPEN'|};
export type CloseAction = {|type: 'CLOSE'|};
export type ToggleApiExplorerAction = {|type: 'TOGGLE_API_EXPLORER'|};
export type InvokeCommandAction = {|
  type: 'INVOKE_COMMAND',
  payload: {
    command?: string,
    inputCommand?: InputCommand,
    paneIdx?: number,
    paneID?: number,
  },
|};
export type UpdatePaneAction = {|
  type: 'UPDATE_PANE',
  payload: {pane: $Shape<PaneState>, paneIdx?: number, paneID?: number},
  navigation: Navigation,
|};
export type ResetAction = {|type: 'RESET'|};
export type LockPanesAction = {|type: 'LOCK_PANES'|};
export type UnlockPanesAction = {|type: 'UNLOCK_PANES'|};
export type PrintOutputAction = {|
  type: 'PRINT_OUTPUT',
  payload: {paneIdx?: number, paneID?: number, output: React.Node},
|};
export type Action =
  | FocusAction
  | AddPaneAction
  | RemovePaneAction
  | FocusPaneAction
  | OpenAction
  | CloseAction
  | InvokeCommandAction
  | UpdatePaneAction
  | ResetAction
  | LockPanesAction
  | UnlockPanesAction
  | PrintOutputAction
  | ToggleApiExplorerAction;

export const reducer = (state: State, action: Action) => {
  try {
    switch (action.type) {
      case 'SET_FOCUS':
        return {...state, isFocused: action.payload};
      case 'ADD_PANE':
        return state.panes.length < MAX_PANE_COUNT
          ? {
              ...state,
              nextPaneID: state.nextPaneID + 1,
              panes: [
                ...state.panes,
                createInitialPaneState({paneID: state.nextPaneID}),
              ],
              focusedPane: state.panes.length,
            }
          : state;
      case 'REMOVE_PANE':
        return {
          ...state,
          panes: [
            ...state.panes.slice(0, action.payload),
            ...state.panes.slice(action.payload + 1),
          ],
          focusedPane: Math.max(0, state.focusedPane - 1),
        };
      case 'FOCUS_PANE':
        return {
          ...state,
          isFocused: true,
          focusedPane: action.payload,
        };
      case 'CLOSE':
        return {...state, isFocused: false, openState: 'closed'};
      case 'OPEN':
        return {...state, isFocused: true, openState: 'open'};
      case 'INVOKE_COMMAND': {
        let paneIdx = state.panes.findIndex(({allowInput}) => allowInput);

        if (action.payload.hasOwnProperty('paneIdx')) {
          paneIdx = action.payload.paneIdx;
        }

        if (action.payload.hasOwnProperty('paneID')) {
          paneIdx = state.panes.findIndex(
            (pane) => pane.paneID === action.payload.paneID,
          );
        }

        paneIdx = paneIdx ?? state.focusedPane;

        const needNewPane = paneIdx === -1;

        if (needNewPane) {
          paneIdx = state.panes.length;
        }

        const panes = needNewPane
          ? [
              ...state.panes,
              {
                ...createInitialPaneState({paneID: state.nextPaneID}),
                command: normalizeCommand(action.payload?.command ?? ''),
                inputCommand: new InputCommand(action.payload?.command ?? ''),
                runCommand: true,
                allowInput: false,
              },
            ]
          : [
              ...state.panes.slice(0, paneIdx),
              {
                ...state.panes[paneIdx],
                command: normalizeCommand(
                  action.payload?.command ?? state.panes[paneIdx].command,
                ),
                inputCommand: new InputCommand(
                  action.payload?.command ?? state.panes[paneIdx].command,
                ),
                runCommand: true,
                allowInput: false,
              },
              ...state.panes.slice(paneIdx + 1),
            ];

        return {
          ...state,
          isFocused: true,
          focusedPane: paneIdx,
          openState: 'open',
          nextPaneID: needNewPane ? state.nextPaneID + 1 : state.nextPaneID,
          panes,
        };
      }
      case 'UPDATE_PANE': {
        let paneIdx;

        if (action.payload.hasOwnProperty('paneIdx')) {
          paneIdx = action.payload.paneIdx;
        }

        if (action.payload.hasOwnProperty('paneID')) {
          paneIdx = state.panes.findIndex(
            (pane) => pane.paneID === action?.payload?.paneID,
          );
        }

        paneIdx = paneIdx ?? state.focusedPane;

        const newPane = {
          ...state.panes[paneIdx],
          ...action.payload.pane,
        };

        if (newPane.cursorIndex === newPane.command.length) {
          const newSuggestions = giveSuggestion(
            new InputCommand(newPane.command),
            tree,
            action.navigation,
          );
          newPane.suggestions = newSuggestions;
        } else {
          // Seems silly but Flow needs this inline annotation to avoid [speculation-ambiguous]
          const emptySuggestions: Array<[string, Command]> = [];
          newPane.suggestions = emptySuggestions;
        }

        if (!action.payload.pane.hasOwnProperty('suggestionIndex')) {
          newPane.suggestionIndex = !isEqual(
            newPane.suggestions,
            state.panes[paneIdx].suggestions,
          )
            ? -1
            : state.panes[paneIdx].suggestionIndex;
        }

        return {
          ...state,
          panes: [
            ...state.panes.slice(0, paneIdx),
            newPane,
            ...state.panes.slice(paneIdx + 1),
          ],
        };
      }
      case 'RESET': {
        return {
          ...createInitialState(),
          isFocused: state.isFocused,
        };
      }
      case 'LOCK_PANES': {
        return {
          ...state,
          locked: true,
        };
      }
      case 'UNLOCK_PANES': {
        return {
          ...state,
          locked: false,
        };
      }
      case 'PRINT_OUTPUT': {
        let paneIdx;

        if (action.payload.hasOwnProperty('paneIdx')) {
          paneIdx = action.payload.paneIdx;
        } else if (action.payload.hasOwnProperty('paneID')) {
          paneIdx = state.panes.findIndex(
            (pane) => pane.paneID === action.payload.paneID,
          );
        }

        paneIdx = paneIdx ?? state.focusedPane;

        const newPane = {
          ...state.panes[paneIdx],
          history: [
            ...state.panes[paneIdx].history,
            {command: '', output: action.payload.output, isPrintOutput: true},
          ],
        };

        return {
          ...state,
          panes: [
            ...state.panes.slice(0, paneIdx),
            newPane,
            ...state.panes.slice(paneIdx + 1),
          ],
        };
      }
      case 'TOGGLE_API_EXPLORER': {
        return {
          ...state,
          showApiExplorer: !state.showApiExplorer,
        };
      }
      default:
        throw new Error(`Unhandled action type: ${action.type}`);
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(err);

    if (Sentry && Sentry.captureException) {
      Sentry.captureException(err);
    }

    return state;
  }
};
